import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X } from "lucide-react";

export const emptyVehicle = () => ({ brand: "", model: "", year_from: "", year_to: "" });

export const vehicleYearStr = (v) => {
  const lo = String(v?.year_from ?? "").trim();
  const hi = String(v?.year_to ?? "").trim();
  if (lo && hi && lo !== hi) return `${Math.min(+lo, +hi)} - ${Math.max(+lo, +hi)}`;
  return lo || hi || "";
};

export const vehicleLabelOf = (v) => [v?.brand, v?.model, vehicleYearStr(v)].filter((x) => String(x || "").trim()).join(" ") || "—";

// Rows the catalog item lives in: the saved `vehicles` list, else the legacy brand / model / year.
export const vehiclesOf = (item) => {
  const vs = (item?.vehicles || []).filter((v) => v && (v.brand || v.model));
  if (vs.length) return vs.map((v) => ({ brand: v.brand || "", model: v.model || "", year_from: v.year_from ?? "", year_to: v.year_to ?? "" }));
  if (!item) return [];
  const m = String(item.year || "").match(/((?:19|20)\d{2})(?:\D+((?:19|20)\d{2}))?/);
  return [{ brand: item.brand || "", model: item.model || "", year_from: m ? m[1] : "", year_to: m ? (m[2] || m[1]) : "" }];
};

export const normalizeVehicles = (rows) =>
  rows
    .map((v) => ({ brand: String(v.brand || "").trim(), model: String(v.model || "").trim(), year_from: String(v.year_from || "").trim(), year_to: String(v.year_to || "").trim() }))
    .filter((v) => v.brand || v.model)
    .map((v) => ({ ...v, year_from: v.year_from ? +v.year_from : null, year_to: v.year_to ? +v.year_to : (v.year_from ? +v.year_from : null) }));

// Brand | Model | From | To per car — a CAT # can fit several cars / year ranges.
export function VehiclesEditor({ rows, onChange, testid = "vehicles" }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const remove = (i) => onChange(rows.length > 1 ? rows.filter((_, j) => j !== i) : [emptyVehicle()]);
  return (
    <div className="space-y-2" data-testid={`${testid}-editor`}>
      <div className="hidden md:grid grid-cols-[1fr_1fr_110px_110px_32px] gap-2 text-[10px] uppercase tracking-widest font-mono text-neutral-500 px-0.5">
        <span>Brand</span><span>Model</span><span>Year from</span><span>Year to</span><span />
      </div>
      {rows.map((v, i) => (
        <div key={i} className="grid grid-cols-2 md:grid-cols-[1fr_1fr_110px_110px_32px] gap-2 items-center" data-testid={`${testid}-row-${i}`}>
          <Input value={v.brand} onChange={(e) => set(i, "brand", e.target.value)} placeholder="Acura" data-testid={`${testid}-brand-${i}`} />
          <Input value={v.model} onChange={(e) => set(i, "model", e.target.value)} placeholder="ILX" data-testid={`${testid}-model-${i}`} />
          <Input value={v.year_from} onChange={(e) => set(i, "year_from", e.target.value)} placeholder="2013" type="number" data-testid={`${testid}-from-${i}`} />
          <Input value={v.year_to} onChange={(e) => set(i, "year_to", e.target.value)} placeholder="2022" type="number" data-testid={`${testid}-to-${i}`} />
          <button type="button" onClick={() => remove(i)} className="h-9 w-8 grid place-items-center rounded-md border border-neutral-300 text-neutral-500 hover:text-red-700 hover:border-red-400 col-span-2 md:col-span-1" title="Remove this car" data-testid={`${testid}-remove-${i}`}>
            <X size={14} />
          </button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="gap-1 h-8 text-xs" onClick={() => onChange([...rows, emptyVehicle()])} data-testid={`${testid}-add`}>
        <Plus size={13} /> Add another car (from year → to year)
      </Button>
      <div className="text-[10px] text-neutral-500 font-mono">The first car is shown in the catalog list; every car listed is accepted by the order vehicle check.</div>
    </div>
  );
}
